import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import PackageCard from '../packages/PackageCard';
import { useUserState } from '../../lib/userState';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Homepage "Live now" strip.
 * Pulls 4 random live H&L packages and renders them above the fold.
 * State-aware: SA visitors see Adelaide inventory (Adelaide + Adelaide
 * Hills + Regional SA regions rolled up under the SA state); VIC
 * visitors see Melbourne + Geelong + Regional VIC packages. Hides
 * itself silently if the feed is empty or unreachable.
 *
 * Strategically placed because:
 *  - The homepage has the highest internal authority on the site.
 *  - It feeds Google fresh, dated content above the fold every nightly crawl.
 *  - Internal-link equity flows straight from `/` into deep H&L detail URLs.
 */

// Per-state UI + query config. The `regions` array maps to the
// backend's `region` field on each listing so we can roll up a state's
// H&L inventory (e.g. VIC = Melbourne + Geelong + Regional VIC + Other-VIC).
const STATE_CONFIG = {
  SA: {
    eyebrowLabel: 'Live now in Adelaide',
    heading: 'house and land packages',
    subline: 'Across Adelaide growth suburbs. Beds, baths and land at a glance. Full price and floor plan inside the BuildPilot App.',
    ctaHref: '/house-and-land-adelaide',
    ctaLabelPrefix: 'View all',
    // Region tags we accept for SA rollup. `Other` is included because
    // the backend flags non-Adelaide-metro SA suburbs as "Other".
    stateCode: 'SA',
    filterQuick: [
      ['3 Bedroom', '/house-and-land-adelaide/filter/3-bedroom'],
      ['4 Bedroom', '/house-and-land-adelaide/filter/4-bedroom'],
      ['Single Storey', '/house-and-land-adelaide/filter/single-storey'],
      ['Under $500K', '/house-and-land-adelaide/filter/under-500k'],
      ['First Home Buyer', '/house-and-land-adelaide/filter/first-home-buyer'],
    ],
  },
  VIC: {
    eyebrowLabel: 'Live now in Victoria',
    heading: 'house and land packages',
    subline: 'Across Melbourne growth corridors, Geelong and regional Victoria. Beds, baths and land at a glance. Full price and floor plan inside the BuildPilot App.',
    ctaHref: '/vic',
    ctaLabelPrefix: 'View all',
    stateCode: 'VIC',
    // VIC filters route into the Victoria hub for now - per-filter
    // deep pages will land with the full Melbourne edition.
    filterQuick: [
      ['Melbourne', '/vic'],
      ['Geelong', '/vic'],
      ['Regional VIC', '/vic'],
      ['3 Bedroom', '/vic'],
      ['4 Bedroom', '/vic'],
    ],
  },
};

const HomepageLivePackagesStrip = () => {
  const userState = useUserState();
  const config = STATE_CONFIG[userState] || STATE_CONFIG.SA;
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        // Pull the full feed and filter client-side by state - the
        // API accepts `region=` but we want the full state rollup
        // (Adelaide + Adelaide Hills for SA, Melbourne + Geelong +
        // Regional VIC for VIC), so filtering on the response is
        // simpler than a fan-out of region queries.
        const r = await fetch(`${API}/api/packages`);
        if (!r.ok) return;
        const j = await r.json();
        if (!alive) return;
        const all = (j.listings || []).filter((li) => li.state === config.stateCode);
        setTotal(all.length);
        const shuffled = [...all].sort(() => Math.random() - 0.5);
        setListings(shuffled.slice(0, 4));
      } catch (_) { /* silent */ } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // Re-fetch when the visitor swaps states so the strip is always
    // aligned with the header chip.
  }, [config.stateCode]);

  if (loading || listings.length === 0) return null;

  return (
    <section className="py-10 sm:py-20 bg-white border-t border-gray-100" data-testid="homepage-live-packages-strip" data-packages-state={userState}>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-center sm:items-end sm:justify-between gap-4 mb-5 sm:mb-8">
          <div className="text-center sm:text-left">
            <h2 className="text-[22px] sm:text-[36px] font-bold text-[#1d1d1f] tracking-tight leading-[1.1]">
              <span className="gradient-text-blue">{total} live</span> {config.heading}
            </h2>
            <p className="text-[15px] text-[#86868b] mt-2 max-w-[640px] mx-auto sm:mx-0 leading-[1.55]">
              {config.subline}
            </p>
          </div>
          <Link
            to={config.ctaHref}
            className="inline-flex items-center justify-center gap-1.5 bg-[#1d1d1f] text-white hover:bg-black px-5 py-3 rounded-full text-[13.5px] font-semibold transition-all shrink-0"
            data-testid="homepage-live-packages-view-all"
          >
            {config.ctaLabelPrefix} {total} packages
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="homepage-live-packages-grid">
          {listings.map((li) => <PackageCard key={li.id} listing={li} />)}
        </div>

        {/* Filter quick-links for internal SEO routing */}
        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-1.5 mt-7">
          <span className="text-xs sm:text-[11px] font-semibold uppercase tracking-[0.06em] text-[#86868b] mr-1">Popular:</span>
          {config.filterQuick.map(([label, href]) => (
            <Link
              key={`${label}-${href}`}
              to={href}
              className="inline-flex items-center gap-1 bg-[#fafbfc] border border-gray-200 hover:border-[#1d1d1f] text-[#1d1d1f] text-[13.5px] sm:text-[12.5px] font-medium px-3.5 py-2 sm:px-3 sm:py-1.5 rounded-full transition-colors min-h-[40px] sm:min-h-0"
            >
              {label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomepageLivePackagesStrip;
